import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import { getEnrollment, verifyEnrollmentPayment } from '../../services/lmsService';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import Footer from '../Footer';
import { CreditCard, Shield, CheckCircle, ArrowLeft, GraduationCap, Calendar, AlertCircle, RefreshCw } from 'lucide-react';

const LMSPayment = () => {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  useEffect(() => {
    if (!user) { navigate('/'); return; }
    fetchEnrollment();
  }, [enrollmentId, user]);
  
  const fetchEnrollment = async () => {
    try {
      setLoading(true);
      const response = await getEnrollment(enrollmentId);
      if (response.success) {
        setEnrollment(response.data);
        if (response.data.payment_status === 'completed') {
          toast.success('Payment already completed!');
          navigate('/my-learning');
        }
      }
    } catch (error) {
      toast.error('Failed to load enrollment');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };
  
  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };
  
  const handlePayment = async () => {
    try {
      setProcessing(true);
      const loaded = await loadRazorpay();
      if (!loaded) { toast.error('Failed to load payment gateway'); return; }
      
      // Get the correct Razorpay key from enrollment or env
      const razorpayKey = enrollment.razorpay_key_id || process.env.REACT_APP_RAZORPAY_KEY_ID;
      
      // Calculate amount with GST
      const baseAmount = enrollment.amount_due || 0;
      const gstAmount = Math.round(baseAmount * 0.18 * 100) / 100;
      const totalAmount = baseAmount + gstAmount;
      
      const options = {
        key: razorpayKey,
        amount: Math.round(totalAmount * 100),  // Razorpay expects amount in paise
        currency: 'INR',
        name: 'Nerd AI',
        description: `Course: ${enrollment.course_title || enrollment.course?.title}`,
        order_id: enrollment.razorpay_order_id,
        handler: async function (response) {
          try {
            const verifyResponse = await verifyEnrollmentPayment({
              enrollment_id: enrollmentId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            if (verifyResponse.success) {
              toast.success('Payment successful!');
              // Redirect to LMS payment success page
              const successUrl = `/lms/payment-success?course=${encodeURIComponent(enrollment.course_title || enrollment.course?.title)}&order_id=${response.razorpay_order_id}`;
              window.location.href = successUrl;
            }
          } catch (error) { 
            console.error('Payment verification failed:', error);
            toast.error('Payment verification failed'); 
          }
        },
        prefill: { name: user.full_name, email: user.email },
        theme: { color: '#9333ea' },
        modal: { ondismiss: function() { setProcessing(false); } }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) { 
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment'); 
    } finally { 
      setProcessing(false); 
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!enrollment) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-2xl mx-auto">
          <Button variant="ghost" className="mb-6" onClick={() => navigate('/courses')}>
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Courses
          </Button>
          
          <Card className="shadow-xl">
            <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <CreditCard className="w-5 h-5 sm:w-6 sm:h-6" />Complete Your Enrollment
              </CardTitle>
            </CardHeader>
            
            <CardContent className="p-4 sm:p-6">
              {/* Course Info */}
              <div className="mb-6 pb-6 border-b">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-xl font-bold text-gray-900">{enrollment.course_title || enrollment.course?.title}</h2>
                    {enrollment.batch_name && <p className="text-gray-500 text-sm">Batch: {enrollment.batch_name}</p>}
                    {enrollment.batch_start_date && (
                      <div className="flex gap-4 mt-2 text-xs sm:text-sm text-gray-500">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3 sm:w-4 sm:h-4" />Starts {new Date(enrollment.batch_start_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    {!enrollment.batch_id && (
                      <p className="text-indigo-600 text-sm mt-2">Self-Paced Course - Start anytime!</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Payment Summary */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-4 text-sm sm:text-base">Payment Summary</h3>
                {(() => {
                  const baseAmount = enrollment.amount_due || 0;
                  const discountAmount = enrollment.discount_amount || 0;
                  const originalAmount = enrollment.original_amount || baseAmount;
                  const gstAmount = Math.round(baseAmount * 0.18 * 100) / 100;
                  const totalAmount = baseAmount + gstAmount;
                  
                  return (
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between"><span className="text-gray-600">Course Fee</span><span className="font-medium">₹{originalAmount.toLocaleString()}</span></div>
                      {discountAmount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>-₹{discountAmount.toLocaleString()}</span></div>}
                      <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-medium">₹{baseAmount.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">GST (18%)</span><span className="font-medium">₹{gstAmount.toLocaleString()}</span></div>
                      <div className="flex justify-between pt-3 border-t text-base sm:text-lg font-bold"><span>Total</span><span className="text-indigo-600">₹{totalAmount.toLocaleString()}</span></div>
                    </div>
                  );
                })()}
              </div>
              
              {/* Security */}
              <div className="mb-6 p-3 sm:p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs sm:text-sm text-gray-600">
                    <p className="font-medium text-gray-900">Secure Payment</p>
                    <p>Your payment is secured by Razorpay.</p>
                  </div>
                </div>
              </div>
              
              {enrollment.payment_status === 'pending' && (
                <div className="mb-6 p-3 sm:p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-2 text-yellow-700 text-sm">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Complete payment to access the course</span>
                  </div>
                </div>
              )}
              
              {(() => {
                const baseAmount = enrollment.amount_due || 0;
                const gstAmount = Math.round(baseAmount * 0.18 * 100) / 100;
                const totalAmount = baseAmount + gstAmount;
                
                return (
                  <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 text-base sm:text-lg py-4 sm:py-6" onClick={handlePayment} disabled={processing}>
                    {processing ? 'Processing...' : <><CreditCard className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />Pay ₹{totalAmount.toLocaleString()}</>}
                  </Button>
                );
              })()}
              
              {/* Features */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium text-gray-900 mb-3 text-sm">What you get:</h4>
                <ul className="space-y-2">
                  {['Full course access', 'Live interactive sessions', 'Recording access', 'Certificate on completion'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs sm:text-sm text-gray-600"><CheckCircle className="w-4 h-4 text-green-500" />{item}</li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LMSPayment;
